import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAudit } from '../../lib/api/admin';
import { queryKeys } from '../../lib/query-client';
import { formatDateTime } from '../../lib/format';
import { useFilters } from '../../hooks/useFilters';
import { useTableState } from '../../hooks/useTableState';
import type { AuditEntry } from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { ExportMenu } from '../../components/ExportMenu';
import { Card, Notice, TableSkeleton } from '../../components/primitives';
import { PageHeading } from '../layout/PageHeading';

/** Auditoría: un interceptor registra toda mutación con antes y después. */
export function AuditPage(): JSX.Element {
  const { filters } = useFilters();
  const table = useTableState({ sortBy: 'occurredAt', sortDir: 'desc', pageSize: 50 });

  const auditQuery = useQuery({
    queryKey: queryKeys.audit({ filters, state: table.state }),
    queryFn: () => fetchAudit(filters, table.state),
  });

  const columns = useMemo<Array<Column<AuditEntry>>>(
    () => [
      {
        key: 'occurredAt',
        header: 'Momento',
        sortable: true,
        render: (row) => formatDateTime(row.occurredAt),
        plain: (row) => row.occurredAt,
      },
      {
        key: 'actorName',
        header: 'Usuario',
        sortable: true,
        render: (row) => (
          <>
            {row.actorName ?? '(sistema)'}
            {row.actorEmail !== null && <div className="tiny muted">{row.actorEmail}</div>}
          </>
        ),
        plain: (row) => row.actorName,
      },
      {
        key: 'module',
        header: 'Módulo',
        sortable: true,
        render: (row) => row.module,
        plain: (row) => row.module,
      },
      {
        key: 'action',
        header: 'Acción',
        sortable: true,
        render: (row) => <span className="mono">{row.action}</span>,
        plain: (row) => row.action,
      },
      {
        key: 'entity',
        header: 'Entidad',
        render: (row) =>
          row.entity === null ? '—' : (
            <>
              {row.entity}
              {row.entityId !== null && <div className="mono tiny muted">{row.entityId}</div>}
            </>
          ),
        plain: (row) => row.entity,
      },
      {
        key: 'summary',
        header: 'Detalle',
        render: (row) => row.summary ?? '—',
        plain: (row) => row.summary,
      },
      {
        key: 'ipAddress',
        header: 'IP',
        render: (row) => <span className="mono">{row.ipAddress ?? '—'}</span>,
        plain: (row) => row.ipAddress,
      },
      {
        key: 'correlationId',
        header: 'Correlación',
        render: (row) => <span className="mono">{row.correlationId ?? '—'}</span>,
        plain: (row) => row.correlationId,
      },
    ],
    [],
  );

  const rows = auditQuery.data;

  return (
    <>
      <PageHeading
        title="Auditoría"
        description="Registro de toda mutación realizada en la plataforma: usuario, acción, módulo, dirección IP y agente del navegador."
        actions={<ExportMenu moduleKey="audit" />}
      />

      <Notice tone="neutral">
        El registro de auditoría no se edita ni se elimina desde la interfaz. Los campos sensibles
        —contraseñas, tokens, RUT— se redactan antes de escribirse.
      </Notice>

      <Card title="Registro de actividad" flush>
        <DataBoundary
          isLoading={auditQuery.isLoading}
          isError={auditQuery.isError}
          error={auditQuery.error}
          onRetry={() => void auditQuery.refetch()}
          isEmpty={rows !== undefined && rows.total === 0 && table.state.search.length === 0}
          empty={{
            title: 'Sin registros de auditoría',
            message: 'No hay actividad registrada para los filtros aplicados.',
          }}
          skeleton={<TableSkeleton columns={8} rows={10} />}
        >
          {rows !== undefined && (
            <DataTable
              caption="Registro de auditoría"
              columns={columns}
              rows={rows.data}
              getRowId={(row) => row.id}
              state={table.state}
              onStateChange={table.setState}
              total={rows.total}
              totalPages={rows.totalPages}
              searchPlaceholder="Buscar por usuario, acción o entidad…"
              isFetching={auditQuery.isFetching}
            />
          )}
        </DataBoundary>
      </Card>
    </>
  );
}
